import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { beautyServices, wellnessServices } from '../../data/services';

export const SplitScreenServices = () => {
  const [activePanel, setActivePanel] = useState(null);

  return (
    <section 
      data-testid="split-screen-services"
      className="h-screen w-full flex flex-col lg:flex-row overflow-hidden"
    >
      {/* Beauty Panel */}
      <motion.div
        className="split-panel relative cursor-pointer overflow-hidden"
        style={{ 
          flex: activePanel === 'wellness' ? 0.3 : activePanel === 'beauty' ? 0.7 : 0.5 
        }}
        onMouseEnter={() => setActivePanel('beauty')}
        onMouseLeave={() => setActivePanel(null)}
        data-testid="beauty-panel"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
          style={{ 
            backgroundImage: `url(${beautyServices.image})`,
            transform: activePanel === 'beauty' ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 transition-all duration-500 ${
          activePanel === 'beauty' 
            ? 'bg-amaranth/70' 
            : activePanel === 'wellness' 
              ? 'bg-dark/80' 
              : 'bg-dark/50'
        }`} />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-white p-8 lg:p-16">
          <motion.div
            initial={false}
            animate={{ 
              opacity: activePanel === 'wellness' ? 0.5 : 1,
              scale: activePanel === 'beauty' ? 1.05 : 1
            }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <p className="font-cinzel text-xs tracking-[0.3em] mb-4 text-white/80">
              {beautyServices.tagline.toUpperCase()}
            </p>
            <h2 className="font-serif text-4xl lg:text-6xl mb-6">
              {beautyServices.category}
            </h2>
            
            {/* Services List - Only show when active */}
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ 
                opacity: activePanel === 'beauty' ? 1 : 0,
                height: activePanel === 'beauty' ? 'auto' : 0
              }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8 max-w-lg mx-auto">
                {beautyServices.services.slice(0, 6).map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    data-testid={`beauty-service-${service.id}`}
                    className="group flex items-center gap-2 text-left hover:text-thulian transition-colors py-2"
                  >
                    <span className="w-2 h-2 rotate-45 bg-thulian group-hover:scale-150 transition-transform" />
                    <span className="text-sm">{service.name}</span>
                  </Link>
                ))}
              </div>
              <Link
                to="/services"
                data-testid="view-all-beauty"
                className="inline-block mt-6 text-sm border-b border-white/50 hover:border-white transition-colors"
              >
                View All Beauty Treatments
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Wellness Panel */}
      <motion.div
        className="split-panel relative cursor-pointer overflow-hidden"
        style={{ 
          flex: activePanel === 'beauty' ? 0.3 : activePanel === 'wellness' ? 0.7 : 0.5 
        }}
        onMouseEnter={() => setActivePanel('wellness')}
        onMouseLeave={() => setActivePanel(null)}
        data-testid="wellness-panel"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
          style={{ 
            backgroundImage: `url(${wellnessServices.image})`,
            transform: activePanel === 'wellness' ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 transition-all duration-500 ${
          activePanel === 'wellness' 
            ? 'bg-brook/70' 
            : activePanel === 'beauty' 
              ? 'bg-dark/80' 
              : 'bg-dark/50'
        }`} />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-white p-8 lg:p-16">
          <motion.div
            initial={false}
            animate={{ 
              opacity: activePanel === 'beauty' ? 0.5 : 1,
              scale: activePanel === 'wellness' ? 1.05 : 1
            }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <p className="font-cinzel text-xs tracking-[0.3em] mb-4 text-white/80">
              {wellnessServices.tagline.toUpperCase()}
            </p>
            <h2 className="font-serif text-4xl lg:text-6xl mb-6">
              {wellnessServices.category}
            </h2>
            
            {/* Services List - Only show when active */}
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ 
                opacity: activePanel === 'wellness' ? 1 : 0,
                height: activePanel === 'wellness' ? 'auto' : 0
              }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8 max-w-lg mx-auto">
                {wellnessServices.services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    data-testid={`wellness-service-${service.id}`}
                    className="group flex items-center gap-2 text-left hover:text-brook transition-colors py-2"
                  >
                    <span className="w-2 h-2 rotate-45 bg-brook group-hover:scale-150 transition-transform" />
                    <span className="text-sm">{service.name}</span>
                  </Link>
                ))}
              </div>
              <Link
                to="/services"
                data-testid="view-all-wellness"
                className="inline-block mt-6 text-sm border-b border-white/50 hover:border-white transition-colors"
              >
                View All Wellness Treatments
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default SplitScreenServices;
